import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { ENV } from '@/lib/config/env';
import { acceptLanguageRequestInterceptor } from './interceptors/accept-language.interceptor';
import { problemDetailsInterceptor } from './interceptors/problem-details.interceptor';
import { refreshTokenInterceptor } from './interceptors/refresh-token.interceptor';
import { workspaceIdRequestInterceptor } from './interceptors/workspace-id.interceptor';

interface ApiClient {
  setConfig: (config: { baseURL: string; withCredentials: boolean }) => void;
  instance: AxiosInstance;
}

export function configureApiClient<T extends ApiClient>(client: T, baseURL: string): T {
  client.setConfig({
    baseURL: baseURL.trim().replace(/\/+$/, ''),
    withCredentials: true,
  });

  if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION && ENV.LOCAL_TOKEN) {
    client.instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      config.headers.Authorization = ENV.LOCAL_TOKEN;
      return config;
    });
  }

  client.instance.interceptors.response.use(
    problemDetailsInterceptor.onResponse,
    async (error: AxiosError) => {
      // Skip token refresh in mock auth mode — the static token can't be refreshed
      if (error.response?.status === 401 && !ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION) {
        return await refreshTokenInterceptor(error, client.instance);
      }

      return problemDetailsInterceptor.onError(error);
    },
  );

  client.instance.interceptors.request.use(workspaceIdRequestInterceptor);
  client.instance.interceptors.request.use(acceptLanguageRequestInterceptor);

  return client;
}
